'use client';

import {
  memo,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import {
  format,
  isPast,
  startOfMonth,
  endOfMonth,
  addDays,
  max,
  isSameMonth,
} from 'date-fns';
import { Badge, toast, Separator, QuantityStepper } from '@mokoboko/components';
import { useQueryClient } from '@tanstack/react-query';
import {
  getUserTimezone,
  formatClassDateTime,
  formatClockTime,
  formatPrice,
  getAbbreviatedName,
  toZonedDateKey,
  type TimeFormat,
} from '@/lib/utils';
import useTenant, { useTimeFormat } from '@/state/tenant';
import { getErrorCode, getErrorMessage } from '@/lib/error-utils';
import { formatEffectivePriceLabel } from '@/helpers/credits';
import {
  buildMinSpotsAllNotice,
  getMinSpotsPerBooking,
} from '@/helpers/min-spots';
import { BookingWarningModal, ClassesLoadError } from '@/components';
import { CalendarSkeleton } from '@/components/class-calendar-skeleton';
import { useAuth } from '@/contexts/auth';
import useClassAvailabilities, {
  forgetClassAvailabilities,
} from '@/state/class-availabilities';
import useEffectivePrice from '@/state/effective-price';
import { Class } from '@/state/class-groups';
import { CartData } from '@/lib/types';
import { trackMetaConversion } from '@/lib/analytics';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import {
  goToCheckout,
  reserveCheckoutTab,
} from '@/helpers/checkout-navigation';
import { rescheduleClass } from '@/services/order';
import {
  isClosedClassTimeRefusal,
  isStaleBookingRefusal,
  orderReceiptPath,
} from '@/helpers/order';
import { isLoginRequired } from '@/lib/http-adapter';
import { LoginDialog } from '@/components/dialogs/login';
import { AppearanceSettings } from '@/state/appearance-settings';
import useMyWaitlistEntries from '@/state/waitlist';
import useBookedClasses from '@/state/booked-classes';
import { bookingReceiptPathFor } from '@/helpers/booked-classes';
import {
  findActiveEntryForSlot,
  getWaitlistAction,
  getWaitlistGate,
  hasWaitlistActivity,
  WAITLIST_ACTION_LABEL,
  type WaitlistAction,
} from '@/helpers/waitlist';
import { classDatePath } from '@/helpers/offering';
import {
  guestAlreadyBookedMessage,
  hasRoomWithGuests,
  noRoomWithGuests,
  rescheduleGuestNote,
} from '@/helpers/guest-pass';
import { openSitePage } from '@/helpers/embed-navigation';
import { useIsEmbed } from '@/contexts/embed';
import { useOpenSitePage } from '@/hooks/use-open-site-page';
import {
  isOneTimeClass,
  linkedClassDate,
  toOccurrenceKey,
} from '@/helpers/class';
import { JoinWaitlistModal } from '@/components/features/waitlist/join-waitlist-modal';
import { useClassDateFilter } from '@/hooks/use-class-date-filter';
import { useBookingGuard } from '@/hooks/use-booking-guard';
import { Calendar, isSlotFull } from './calendar';
import type { CalendarSlot } from './calendar';
import { InstructorFilter } from './instructor-filter';
import { OneTimeBooking, OneTimeBookingSkeleton } from './one-time-booking';
import {
  transformClassAvailability,
  getUniqueInstructors,
  normalizeInstructorSelection,
  restoreSelectedInstructorIds,
} from '@/helpers/calendar-transform';
import {
  findSameSlot,
  toDateKey,
  getFirstAvailableDate,
  getFirstClassMonth,
  hasFullSlot,
} from './calendar/calendar-utils';

/**
 * The state behind a Book class button drawn outside the calendar. `book`
 * opens her booking, the waitlist or checkout, as the slot needs.
 */
export interface ClassBookingAction {
  book: () => void;
  isProcessing: boolean;
  isFullyBooked: boolean;
  waitlistAction: WaitlistAction | null;
  // She holds a spot on the sold-out class; book() opens her booking.
  isBooked: boolean;
  priceLabel: string | null;
}

const getAbbreviatedInstructorName = (
  name: string | null | undefined
): string => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return getAbbreviatedName(parts[0], parts.slice(1).join(' '));
};

const buildSlotTimeFormatter =
  (timeFormat: TimeFormat) => (startTime: string, timezone: string) =>
    formatClockTime(startTime, timeFormat, timezone);

const bookActionLabelOf = (
  isBooked: boolean,
  waitlistAction: WaitlistAction | null,
  isRescheduling: boolean
): string =>
  isBooked
    ? 'View booking'
    : waitlistAction
      ? WAITLIST_ACTION_LABEL[waitlistAction]
      : isRescheduling
        ? 'Reschedule'
        : 'Continue';

export const ClassCalendar = memo(function ClassCalendar({
  classData,
  isRescheduling = false,
  orderId,
  slotId,
  classSettings,
  currentStartTime,
  rescheduleActiveQuantity = 1,
  onActionChange,
  rescheduleGuests = 0,
  onInstructorClick,
}: {
  classData: Class;
  isRescheduling?: boolean;
  orderId?: string | null;
  slotId?: string | null;
  classSettings: AppearanceSettings['class'];
  currentStartTime?: string | null;
  rescheduleActiveQuantity?: number;
  onActionChange: (action: ClassBookingAction | null) => void;
  // How many of the host's guests sit at the class time being moved.
  rescheduleGuests?: number;
  onInstructorClick?: (adminUserId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [rescheduleQuantity, setRescheduleQuantity] = useState(1);
  const minSpots = getMinSpotsPerBooking(classData);
  // A class with a minimum is moved whole: a partial move would leave or create
  // a booking below it.
  const showRescheduleQuantityPicker =
    isRescheduling && rescheduleActiveQuantity > 1 && minSpots <= 1;
  const showMoveAllNotice =
    isRescheduling && rescheduleActiveQuantity > 1 && minSpots > 1;
  const rescheduleQty = showRescheduleQuantityPicker
    ? Math.min(Math.max(rescheduleQuantity, 1), rescheduleActiveQuantity)
    : rescheduleActiveQuantity;
  const isPartialMove = rescheduleQty < rescheduleActiveQuantity;
  // Guests go with the host's last spot there and stay with a spot kept.
  const movingGuests = isPartialMove ? 0 : rescheduleGuests;
  const guestNote = isRescheduling
    ? rescheduleGuestNote(rescheduleGuests, !isPartialMove)
    : null;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const didInitFromUrl = useRef(false);
  const didInitInstructors = useRef(false);
  const userNavigatedMonth = useRef(false);
  const currentMonthRef = useRef<Date>(null!);
  const [linkedDate] = useState(() =>
    linkedClassDate(searchParams.get('date'))
  );

  const {
    state: {
      selectedDate: hookSelectedDate,
      selectedInstructorIds: hookInstructorIds,
      isRestoredFromStorage,
    },
    isInitialized,
    setSelectedDate: setHookSelectedDate,
    setSelectedInstructorIds: setHookInstructorIds,
    markAsNotRestored,
  } = useClassDateFilter(classData.slug);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<CalendarSlot | null>(null);
  const [instructorIds, setInstructorIds] = useState<string[]>([]);
  const [hasRestoredDate, setHasRestoredDate] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [waitlistSlot, setWaitlistSlot] = useState<CalendarSlot | null>(null);
  const { user } = useAuth();
  const { data: tenant } = useTenant();
  const isEmbed = useIsEmbed();
  const timeFormat = useTimeFormat();
  const formatSlotTime = buildSlotTimeFormatter(timeFormat);

  const timezone = getUserTimezone();
  const isOneTime = isOneTimeClass(classData);

  currentMonthRef.current = currentMonth;

  const today = new Date();
  const isCurrentCalendarMonth =
    currentMonth.getMonth() === today.getMonth() &&
    currentMonth.getFullYear() === today.getFullYear();

  const todayKey = format(today, 'yyyy-MM-dd');
  const upcomingEndKey = format(
    max([endOfMonth(today), addDays(today, 30)]),
    'yyyy-MM-dd'
  );

  const endDate = isCurrentCalendarMonth
    ? upcomingEndKey
    : format(endOfMonth(currentMonth), 'yyyy-MM-dd');

  const startDate = isPast(startOfMonth(currentMonth))
    ? todayKey
    : format(startOfMonth(currentMonth), 'yyyy-MM-dd');

  // The month window exists to feed a grid the customer pages through. A
  // one-time class renders no grid, so a date outside the window would be
  // unreachable rather than a month away: ask for the class's own day instead.
  // The API reads the window as days in `timezone`, so the day is taken from
  // the start instant in that zone, not from the studio's date.
  const oneTimeDay =
    isOneTime && classData.next_occurrence_start
      ? toZonedDateKey(new Date(classData.next_occurrence_start), timezone)
      : null;

  const {
    data: classAvailabilities,
    isLoading: isClassLoading,
    isFetching: isClassFetching,
    error: availabilityError,
    refetch: refetchAvailability,
  } = useClassAvailabilities({
    slug: classData.slug,
    start_date: oneTimeDay ?? startDate,
    end_date: oneTimeDay ?? endDate,
    timezone,
    enabled: !!classData?.class_id,
  });

  // The same request as the month query while this month is on screen. The
  // month query only knows the month shown, and the instructor ticks must not
  // lock a month another instructor teaches in. It only feeds the month grid,
  // which a one-time class does not draw.
  const { data: upcomingAvailabilities } = useClassAvailabilities({
    slug: classData.slug,
    start_date: todayKey,
    end_date: upcomingEndKey,
    timezone,
    enabled: !!classData?.class_id && !isOneTime,
  });

  const firstClassMonth = useMemo(
    () =>
      getFirstClassMonth(
        transformClassAvailability(
          upcomingAvailabilities,
          [],
          isRescheduling ? slotId : undefined,
          isRescheduling ? currentStartTime : undefined
        )
      ),
    [upcomingAvailabilities, isRescheduling, slotId, currentStartTime]
  );

  const instructors = useMemo(
    () => getUniqueInstructors(classAvailabilities),
    [classAvailabilities]
  );

  const availability = useMemo(
    () =>
      transformClassAvailability(
        classAvailabilities,
        // A one-time class draws no instructor filter, so its one slot is read
        // whoever teaches it.
        isOneTime ? [] : instructorIds,
        isRescheduling ? slotId : undefined,
        isRescheduling ? currentStartTime : undefined
      ),
    [
      classAvailabilities,
      isOneTime,
      instructorIds,
      isRescheduling,
      slotId,
      currentStartTime,
    ]
  );

  // Cache slots for the selected date so they survive month switches
  const selectedDateSlotsRef = useRef<CalendarSlot[]>([]);
  useEffect(() => {
    if (!selectedDate) {
      selectedDateSlotsRef.current = [];
      return;
    }
    const key = toDateKey(selectedDate);
    const slots = availability[key];
    if (slots && slots.length > 0) {
      selectedDateSlotsRef.current = slots;
    }
  }, [selectedDate, availability]);

  const effectiveAvailability = useMemo(() => {
    if (!selectedDate) return availability;
    const key = toDateKey(selectedDate);
    if (availability[key]?.length > 0) return availability;
    if (selectedDateSlotsRef.current.length > 0) {
      return { ...availability, [key]: selectedDateSlotsRef.current };
    }
    return availability;
  }, [availability, selectedDate]);

  const { data: myWaitlistEntries } = useMyWaitlistEntries({
    enabled: !isRescheduling && hasWaitlistActivity(effectiveAvailability),
  });
  const { data: bookedClasses } = useBookedClasses({
    email: user?.email ?? '',
    enabled: !isRescheduling && hasFullSlot(effectiveAvailability),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (instructors.length > 0 && !didInitInstructors.current) {
      didInitInstructors.current = true;
      if (hookInstructorIds.length > 0) {
        const restoredIds = restoreSelectedInstructorIds(
          hookInstructorIds,
          instructors
        );
        setInstructorIds(restoredIds);
        setHookInstructorIds(restoredIds);
        // Until now every instructor showed, so the day cache may hold times
        // the restored filter hides.
        selectedDateSlotsRef.current = [];
      }
    }
  }, [instructors, hookInstructorIds]);

  useEffect(() => {
    if (didInitFromUrl.current || !isInitialized) return;

    const urlInstructor = searchParams.get('instructor_id');

    if (linkedDate) {
      setSelectedDate(linkedDate);
      setCurrentMonth(linkedDate);
      setHookSelectedDate(linkedDate);
    }

    if (urlInstructor) {
      const ids = urlInstructor.split(',').filter(Boolean);
      setInstructorIds(ids);
      setHookInstructorIds(ids);
    }

    didInitFromUrl.current = true;
  }, [searchParams, isInitialized, setHookSelectedDate, setHookInstructorIds]);

  useEffect(() => {
    if (!isRestoredFromStorage) return;

    // A date link (the class checkout reminder's) names the day to open on, so
    // the day this tab picked on an earlier visit gives way to it instead of
    // replacing it on screen and in the address.
    if (hookSelectedDate && !linkedDate) {
      if (isPast(hookSelectedDate) || isRescheduling || isOneTime) {
        setHookSelectedDate(null);
      } else {
        setSelectedDate(hookSelectedDate);
        setCurrentMonth(hookSelectedDate);
        setHasRestoredDate(true);
      }
    }

    markAsNotRestored();
  }, [isRestoredFromStorage, hookSelectedDate, markAsNotRestored, isOneTime]);

  useEffect(() => {
    if (!didInitFromUrl.current || !isInitialized) return;

    const params = new URLSearchParams(searchParams.toString());
    const dateISO = selectedDate ? toDateKey(selectedDate) : null;

    if (dateISO) {
      params.set('date', dateISO);
    } else {
      params.delete('date');
    }

    if (instructorIds.length > 0) {
      params.set('instructor_id', instructorIds.join(','));
    } else {
      params.delete('instructor_id');
    }

    const next = `${pathname}?${params.toString()}`;
    const current = `${pathname}?${searchParams.toString()}`;
    // Native history rather than router.replace: the class page is rendered
    // on the server, so a router navigation would run it and its metadata
    // again, each an uncached offering lookup, on every date or instructor
    // change. Next keeps useSearchParams in sync with replaceState.
    if (next !== current) {
      window.history.replaceState(null, '', next);
    }
  }, [selectedDate, instructorIds, pathname, searchParams, isInitialized]);

  useLayoutEffect(() => {
    // A one-time class has no date to choose: its one slot is read directly.
    if (
      isOneTime ||
      !classData?.class_id ||
      isRestoredFromStorage ||
      hasRestoredDate
    )
      return;

    if (userNavigatedMonth.current) {
      userNavigatedMonth.current = false;
      return;
    }

    if (Object.keys(availability).length === 0) {
      // availability is empty while loading and again before the instructor
      // filter initializes; wiping the URL-provided selection in those
      // windows would make auto-select override valid shared links
      if (selectedDate && !isClassFetching && didInitInstructors.current)
        setSelectedDate(null);
      return;
    }

    const cm = currentMonthRef.current;
    const selectedInDisplayedMonth =
      selectedDate &&
      selectedDate.getMonth() === cm.getMonth() &&
      selectedDate.getFullYear() === cm.getFullYear();
    const dateInAvailability =
      selectedInDisplayedMonth &&
      availability[toDateKey(selectedDate)]?.length > 0;

    if (!dateInAvailability) {
      const firstDate = getFirstAvailableDate(availability);
      if (firstDate) {
        const firstInDisplayedMonth =
          firstDate.getMonth() === cm.getMonth() &&
          firstDate.getFullYear() === cm.getFullYear();

        setSelectedDate(firstDate);
        setSelectedSlot(null);
        setHookSelectedDate(firstDate);
        if (!firstInDisplayedMonth) {
          setCurrentMonth(
            new Date(firstDate.getFullYear(), firstDate.getMonth(), 1)
          );
        }
      } else if (selectedDate) {
        setSelectedDate(null);
        setSelectedSlot(null);
      }
    }
  }, [
    availability,
    classData?.class_id,
    isRestoredFromStorage,
    hasRestoredDate,
    selectedDate,
    isOneTime,
  ]);

  // DayPicker shows the lock month for any month before it, so a month state
  // left there (a click back before the lock loaded) would put the empty-month
  // card over a month that has classes.
  useLayoutEffect(() => {
    if (firstClassMonth && currentMonth < firstClassMonth)
      setCurrentMonth(firstClassMonth);
  }, [firstClassMonth, currentMonth]);

  useEffect(() => {
    if (
      selectedSlot &&
      instructorIds.length > 0 &&
      !instructorIds.includes(selectedSlot.instructorId ?? '')
    ) {
      setSelectedSlot(null);
    }
  }, [instructorIds, selectedSlot]);

  const handleMonthChange = useCallback(
    (month: Date) => {
      const direction =
        month > currentMonthRef.current ? 'forward' : 'backward';
      setCurrentMonth(month);
      userNavigatedMonth.current = true;
      track({
        event: AnalyticsEvent.CALENDAR_NAVIGATED,
        properties: {
          class_name: classData.name,
          direction,
          target_month: format(month, 'yyyy-MM'),
        },
      });
    },
    [classData.name]
  );

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      setSelectedSlot(null);
      setHasRestoredDate(false);
      setHookSelectedDate(date);
    },
    [setHookSelectedDate]
  );

  const handleSlotSelect = useCallback(
    (slot: CalendarSlot | null) => {
      setSelectedSlot(slot);
      if (slot) {
        track({
          event: AnalyticsEvent.SLOT_SELECTED,
          properties: {
            class_name: classData.name,
            class_id: classData.class_id,
            instructor_name: slot.instructorName ?? '',
            slot_date: slot.startTime
              ? format(new Date(slot.startTime), 'yyyy-MM-dd')
              : '',
            slot_time: slot.startTime
              ? format(new Date(slot.startTime), 'HH:mm')
              : '',
            available_spots: slot.availableSpots ?? null,
            mode: isRescheduling ? 'reschedule' : 'book',
          },
        });
      }
    },
    [classData.name, classData.class_id, isRescheduling]
  );

  const handleInstructorChange = useCallback(
    (ids: string[]) => {
      const next = normalizeInstructorSelection(ids, instructors);
      // A date kept from the last visit stops the first-available pick; a new
      // instructor choice should move off a day they do not teach.
      setHasRestoredDate(false);
      setInstructorIds(next);
      setHookInstructorIds(next);
      selectedDateSlotsRef.current = [];
      track({
        event: AnalyticsEvent.INSTRUCTOR_FILTERED,
        properties: {
          class_name: classData.name,
          selected_count: next.length || instructors.length,
          total_count: instructors.length,
        },
      });
    },
    [setHookInstructorIds, classData.name, instructors]
  );

  const proceedToCheckout = useCallback(
    (slot: CalendarSlot, reservedTab?: Window | null) => {
      const instructorName = getAbbreviatedInstructorName(slot.instructorName);
      const price = classData.price ?? 0;

      const cartData: CartData<'class'> = {
        items: [
          {
            class_id: classData.class_id,
            slug: classData.slug,
            name: classData.name,
            price,
            quantity: minSpots,
            instructor_name: instructorName,
            ...toOccurrenceKey(slot),
            end_time: slot.endTime,
            timezone: slot.timezone,
            available_spots: slot.availableSpots ?? null,
            min_spots_per_booking: minSpots,
            instructor_schedule_id: slot.instructorScheduleId ?? '',
            currency: classData.currency,
            image: classData.image,
            kind: classData.kind,
          },
        ],
        totalAmount: price * minSpots,
        checkoutType: 'class',
      };

      if (!goToCheckout(router, cartData, reservedTab)) {
        toast.info('Allow pop-ups to continue to checkout.');
        return;
      }

      trackMetaConversion({
        event: 'AddToCart',
        custom_data: {
          content_name: classData.name,
          content_type: 'class',
          content_ids: [classData.slug],
          value: price,
          currency: classData.currency,
        },
      });
      track({
        event: AnalyticsEvent.PRODUCT_ADDED_TO_CART,
        properties: {
          product_type: 'class',
          product_name: classData.name,
          product_id: classData.class_id,
          price: classData.price,
          currency: classData.currency ?? null,
          quantity: minSpots,
        },
      });
    },
    [classData, minSpots, router]
  );

  // Clearing the day cache is the load-bearing half: effectiveAvailability
  // re-injects it whenever the refetched day comes back empty, which would put
  // the slot the user was just blocked on straight back on screen.
  const handleSeeOtherTimes = useCallback(() => {
    setSelectedSlot(null);
    selectedDateSlotsRef.current = [];
    refetchAvailability();
  }, [refetchAvailability]);

  const openPage = useOpenSitePage();
  const {
    warning: bookingWarning,
    isOpen: bookingWarningOpen,
    isChecking: rebookChecking,
    guardBooking,
    handleConfirm: handleBookingWarningConfirm,
    handleOpenChange: handleBookingWarningOpenChange,
  } = useBookingGuard<CalendarSlot>(
    'class_page',
    { className: classData.name, classId: classData.class_id },
    proceedToCheckout,
    isOneTime
      ? () => openPage('/classes', 'Allow pop-ups to see all classes.')
      : handleSeeOtherTimes
  );

  // A full class time she already holds opens her booking: the server would
  // refuse her a place on its waitlist.
  const bookedPathOf = useCallback(
    (slot: CalendarSlot) =>
      isRescheduling || !isSlotFull(slot)
        ? null
        : bookingReceiptPathFor(bookedClasses, toOccurrenceKey(slot)),
    [isRescheduling, bookedClasses]
  );
  const isSlotBooked = useCallback(
    (slot: CalendarSlot) => bookedPathOf(slot) !== null,
    [bookedPathOf]
  );

  // Rescheduling never offers the waitlist in v1. The tile state, the button
  // label and the click all read this one action, so they cannot disagree.
  const waitlistActionOf = useCallback(
    (slot: CalendarSlot) =>
      isRescheduling || !isSlotFull(slot) || isSlotBooked(slot)
        ? null
        : getWaitlistAction(
            getWaitlistGate({
              startTime: slot.startTime,
              isFullyBooked: true,
              waitlistEnabled: slot.waitlistEnabled,
              waitlistCount: slot.waitlistCount,
              config: tenant?.waitlist_config,
            }) === null,
            findActiveEntryForSlot(myWaitlistEntries, toOccurrenceKey(slot))
          ),
    [isRescheduling, tenant?.waitlist_config, myWaitlistEntries, isSlotBooked]
  );
  const isSlotWaitlistJoinable = useCallback(
    (slot: CalendarSlot) => waitlistActionOf(slot) !== null,
    [waitlistActionOf]
  );

  // A one-time class asks for one day, so its slot is the first one there.
  const oneTimeSlot = isOneTime
    ? (Object.values(availability)[0]?.[0] ?? null)
    : null;

  // The copy saved on click goes stale when availability refetches (a spot
  // opens, the cut-off passes), so every read takes the latest one. A one-time
  // class saves no copy: its one slot is the selection, so a refetch cannot
  // leave it stale.
  const liveSelectedSlot = isOneTime
    ? oneTimeSlot
    : ((selectedSlot && selectedDate
        ? findSameSlot(
            effectiveAvailability[toDateKey(selectedDate)],
            selectedSlot
          )
        : undefined) ?? selectedSlot);
  const selectedWaitlistAction = liveSelectedSlot
    ? waitlistActionOf(liveSelectedSlot)
    : null;
  const selectedBookedPath = liveSelectedSlot
    ? bookedPathOf(liveSelectedSlot)
    : null;
  const selectedSlotEntry = liveSelectedSlot
    ? findActiveEntryForSlot(
        myWaitlistEntries,
        toOccurrenceKey(liveSelectedSlot)
      )
    : undefined;

  const handleBook = useCallback(
    async (date: Date, slot: CalendarSlot) => {
      if (isRescheduling) {
        if (!orderId || !slot.instructorScheduleId) {
          toast.error('Unable to reschedule class');
          return;
        }
        if (
          !hasRoomWithGuests(slot.availableSpots, rescheduleQty, movingGuests)
        ) {
          toast.error(noRoomWithGuests(movingGuests));
          return;
        }
        try {
          setIsProcessing(true);
          // Pushed by the receipt — scopes the move to the source occurrence
          // when an order spans multiple slots.
          const fromSlotId = searchParams?.get('fromSlotId') ?? null;
          const fromOverrideSlotId =
            searchParams?.get('fromOverrideSlotId') ?? null;
          await rescheduleClass(orderId, {
            ...toOccurrenceKey(slot),
            instructor_schedule_id: slot.instructorScheduleId,
            end_time: slot.endTime,
            timezone: timezone,
            ...(rescheduleActiveQuantity > 1
              ? { quantity: rescheduleQty }
              : {}),
            ...(fromSlotId
              ? { from_slot_id: fromSlotId }
              : fromOverrideSlotId
                ? { from_override_slot_id: fromOverrideSlotId }
                : {}),
            ...((fromSlotId || fromOverrideSlotId) && currentStartTime
              ? { from_start_time: currentStartTime }
              : {}),
          });
          toast.success(
            isPartialMove
              ? `${rescheduleQty} spot${rescheduleQty > 1 ? 's' : ''} rescheduled!`
              : 'Class rescheduled!'
          );
          track({
            event: AnalyticsEvent.CLASS_RESCHEDULED,
            properties: {
              class_name: classData.name,
              class_id: classData.class_id,
              order_id: orderId,
              new_slot_date: format(new Date(slot.startTime), 'yyyy-MM-dd'),
              new_slot_time: format(new Date(slot.startTime), 'HH:mm'),
            },
          });
          queryClient.invalidateQueries({ queryKey: ['bookedClasses'] });
          queryClient.invalidateQueries({ queryKey: ['order', orderId] });
          // Redirect to the new class time's receipt so the customer sees the
          // spot they just moved (in an order with several class times, the
          // original one may still have other spots and its own receipt).
          router.push(
            orderReceiptPath(
              orderId,
              null,
              { slotId: slot.id, startTime: slot.startTime },
              'rescheduled'
            )
          );
        } catch (error: any) {
          // The receipt gates the Reschedule button on auth, so this is a
          // session that expired between opening the receipt and confirming.
          if (isLoginRequired(error)) {
            setIsLoginOpen(true);
          } else if (isStaleBookingRefusal(error)) {
            // The stale ids can be in this URL, so a reload would send them
            // again and Back must not return to it. The receipt re-reads the
            // order and builds a fresh reschedule link from it, on the class
            // time being rescheduled when the order has several: by its slot,
            // or by its time when the move replaced that slot too.
            toast.error('This booking has changed. Please try again.');
            forgetClassAvailabilities(queryClient);
            queryClient.invalidateQueries({ queryKey: ['bookedClasses'] });
            queryClient.invalidateQueries({ queryKey: ['order', orderId] });
            router.replace(
              orderReceiptPath(orderId, null, {
                slotId,
                startTime: currentStartTime,
              })
            );
          } else if (getErrorCode(error) === 'GUEST_ALREADY_BOOKED') {
            toast.error(guestAlreadyBookedMessage(movingGuests));
          } else if (getErrorCode(error) === 'SLOT_FULL' && movingGuests > 0) {
            toast.error(noRoomWithGuests(movingGuests));
            handleSeeOtherTimes();
          } else if (isClosedClassTimeRefusal(error)) {
            // The booking itself is unchanged, so the customer stays here and
            // picks another time from a grid read afresh.
            toast.error(getErrorMessage(error, 'Unable to reschedule class'));
            handleSeeOtherTimes();
          } else {
            toast.error(getErrorMessage(error, 'Unable to reschedule class'));
          }
        } finally {
          setIsProcessing(false);
        }
        return;
      }

      const bookedPath = bookedPathOf(slot);
      if (bookedPath) {
        router.push(bookedPath);
        return;
      }

      // A waitlist slot: no checkout tab, no duplicate-booking guard — the
      // eligibility endpoint reports ALREADY_WAITLISTED itself, and
      // ALREADY_BOOKED only when her booked list missed the booking (read
      // before it, or an order still PENDING). A full slot with no waitlist
      // action goes on to checkout, which answers SLOT_FULL.
      const waitlistAction = waitlistActionOf(slot);
      if (waitlistAction === 'view' && selectedSlotEntry) {
        router.push(`/waitlist/${selectedSlotEntry.waitlist_entry_id}`);
        return;
      }
      if (waitlistAction === 'join') {
        // Joining needs a first-party session, which a framed embed never
        // has, so the embed hands off to the studio's own class page.
        if (isEmbed) {
          if (!openSitePage(classDatePath(classData.slug, slot.startTime))) {
            toast.info('Allow pop-ups to join the waitlist.');
          }
          return;
        }
        setWaitlistSlot(slot);
        return;
      }

      // The duplicate-booking check is a network round trip, and an embed opens
      // checkout in a new tab — so claim the tab while the click still counts.
      const reservedTab = reserveCheckoutTab();

      if (user?.email) {
        const occurrence = toOccurrenceKey(slot);
        const warned = await guardBooking({
          email: user.email,
          slotId: occurrence.slot_id,
          overrideSlotId: occurrence.override_slot_id,
          startTime: occurrence.start_time,
          instructorScheduleId: slot.instructorScheduleId,
          timezone: slot.timezone,
          payload: slot,
        });
        if (warned) {
          reservedTab?.close();
          return;
        }
      }

      proceedToCheckout(slot, reservedTab);
    },
    [
      isRescheduling,
      orderId,
      classData,
      timezone,
      router,
      queryClient,
      searchParams,
      rescheduleQty,
      isPartialMove,
      movingGuests,
      rescheduleActiveQuantity,
      user?.email,
      proceedToCheckout,
      guardBooking,
      bookedPathOf,
      waitlistActionOf,
      selectedSlotEntry,
      isEmbed,
      handleSeeOtherTimes,
    ]
  );

  const showPrice = classSettings.show_price && !isRescheduling;

  const { data: effectivePriceData, isLoading: isEffectivePriceLoading } =
    useEffectivePrice({
      class_id: classData.class_id,
      // Only a time she is queued for can price differently: her held credit
      // is spendable there and nowhere else. A waitlist slot hides the price
      // badge, so its price is asked without the occurrence.
      occurrence: selectedWaitlistAction ? null : selectedSlotEntry?.occurrence,
      enabled: showPrice,
    });
  const ep = effectivePriceData?.effective_price;

  const cashLabel = formatPrice(classData.price, classData.currency);
  const resolvedLabel = formatEffectivePriceLabel(ep, cashLabel);
  // The cash price is only a fallback for "no credit price applies". Showing it
  // while the query is still in flight advertises Free on a credit-only class.
  const priceBadgeLabel =
    showPrice && !isEffectivePriceLoading ? resolvedLabel : null;
  // The join modal explains the credit hold; a price badge on a waitlist slot
  // or on her own booking would promise a purchase that isn't happening.
  const bookPriceLabel =
    selectedWaitlistAction || selectedBookedPath ? null : priceBadgeLabel;

  const isLoading = isClassLoading && !classAvailabilities;
  // A failed read must not render as a class with no times: the one-time panel
  // would name a booking rule, the grid would say no classes. Retries are off,
  // so the error offers Try again.
  const availabilityFailed = !!availabilityError && !classAvailabilities;

  // Calendar.Root binds the live selection into its own zero-argument handler;
  // a button outside the calendar binds the live slot through this ref, so the
  // copy published below changes only when what the button shows changes, not
  // each time handleBook is rebuilt.
  const bookSelectedRef = useRef<() => void>(() => {});
  bookSelectedRef.current = () => {
    if (liveSelectedSlot) {
      handleBook(new Date(liveSelectedSlot.startTime), liveSelectedSlot);
    }
  };
  const isBusy = isProcessing || rebookChecking;
  // A slot that cannot be acted on is sold out, and the button says so. With no
  // slot at all the panel says why, and a dead button beside it says nothing.
  const showsOneTimeAction = isOneTime && !!oneTimeSlot;
  const isOneTimeFull = !!oneTimeSlot && isSlotFull(oneTimeSlot);

  // A one-time class draws its own Book class button in the page header, where
  // packs and subscriptions put theirs, so the state behind it has to leave
  // this component. Every other page publishes null, which changes nothing.
  useEffect(() => {
    onActionChange(
      showsOneTimeAction
        ? {
            book: () => bookSelectedRef.current(),
            isProcessing: isBusy,
            isFullyBooked: isOneTimeFull,
            waitlistAction: selectedWaitlistAction,
            isBooked: !!selectedBookedPath,
            priceLabel: bookPriceLabel,
          }
        : null
    );
  }, [
    onActionChange,
    showsOneTimeAction,
    isBusy,
    isOneTimeFull,
    selectedWaitlistAction,
    selectedBookedPath,
    bookPriceLabel,
  ]);

  // The first month that can be shown, so "Go to available month" never
  // falls back to the month it is on.
  const isFirstShownMonth = firstClassMonth
    ? isSameMonth(currentMonth, firstClassMonth)
    : isCurrentCalendarMonth;
  const noAvailability =
    isFirstShownMonth && Object.keys(availability).length === 0;
  const isInstructorFiltered = instructorIds.length > 0;
  const emptyDescription = isInstructorFiltered
    ? 'No classes are scheduled for the selected instructor.'
    : 'There are no classes scheduled in the coming weeks.';

  const bookingHeading = <h2 className="heading-section">Pick a time</h2>;

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {isRescheduling && (
        <div className="flex flex-col gap-2">
          <p className="heading-card">
            Rescheduling &ldquo;{classData.name}&rdquo;
          </p>
          {currentStartTime && (
            <p className="text-sm text-gray-600">
              Currently booked for{' '}
              <span className="font-semibold text-gray-900">
                {formatClassDateTime(
                  currentStartTime,
                  timeFormat,
                  timezone,
                  'long'
                )}
              </span>
            </p>
          )}
          {showRescheduleQuantityPicker && (
            <div className="mt-1 flex flex-col gap-2 rounded-lg bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-900">
                You have {rescheduleActiveQuantity} spots booked. How many would
                you like to move?
              </p>
              <QuantityStepper
                value={rescheduleQuantity}
                max={rescheduleActiveQuantity}
                onValueChange={setRescheduleQuantity}
                decreaseLabel="Decrease spots to move"
                increaseLabel="Increase spots to move"
              />
            </div>
          )}
          {showMoveAllNotice && (
            <div className="mt-1 rounded-lg bg-gray-50 px-4 py-3">
              <p className="text-sm text-gray-900">
                {buildMinSpotsAllNotice(
                  minSpots,
                  rescheduleActiveQuantity,
                  'moved'
                )}
              </p>
            </div>
          )}
          {guestNote && (
            <div className="mt-1 rounded-lg bg-gray-50 px-4 py-3">
              <p className="text-sm text-gray-900">{guestNote}</p>
            </div>
          )}
        </div>
      )}

      {isRescheduling && <Separator />}

      <div className="flex flex-col gap-4">
        {/* The one-time panel heads itself and has no time to pick. */}
        {!isOneTime &&
          (instructors.length > 1 ? (
            <InstructorFilter
              heading={bookingHeading}
              instructors={instructors}
              selectedIds={instructorIds}
              onChange={handleInstructorChange}
            />
          ) : (
            bookingHeading
          ))}

        {isLoading ? (
          isOneTime ? (
            <OneTimeBookingSkeleton
              showInstructor={classSettings.show_instructor_name}
            />
          ) : (
            <CalendarSkeleton />
          )
        ) : availabilityFailed ? (
          <ClassesLoadError title="We couldn't load class times" />
        ) : (
          <Calendar.Root
            month={currentMonth}
            minDate={firstClassMonth ?? undefined}
            onMonthChange={handleMonthChange}
            availability={effectiveAvailability}
            selected={selectedDate}
            onSelect={handleDateSelect}
            selectedSlot={liveSelectedSlot}
            onSlotSelect={handleSlotSelect}
            onBook={handleBook}
            bookLabel={bookActionLabelOf(
              !!selectedBookedPath,
              selectedWaitlistAction,
              isRescheduling
            )}
            showSpots={!!classSettings?.show_available_slots}
            spotsUrgencyOnly={!classSettings?.show_available_slots}
            showFullyBooked
            formatSlotTime={formatSlotTime}
            showInstructor={classSettings.show_instructor_name}
            soleInstructor={classData.instructor_schedules?.length === 1}
            isProcessing={isBusy}
            isSlotWaitlistJoinable={isSlotWaitlistJoinable}
            isSlotBooked={isSlotBooked}
          >
            {isOneTime ? (
              <OneTimeBooking
                classData={classData}
                slot={oneTimeSlot}
                showDuration={!!classSettings.show_duration}
                onInstructorClick={onInstructorClick}
              />
            ) : (
              <>
                <div className="relative min-w-0 flex-1 basis-full md:basis-[290px]">
                  <Calendar.Grid />
                  {!isClassFetching && (
                    <Calendar.EmptyMonth
                      noAvailability={noAvailability}
                      noAvailabilityDescription={emptyDescription}
                    />
                  )}
                </div>
                <Calendar.TimeSlots
                  className="md:self-start"
                  emptyMonthTitle={
                    noAvailability ? 'No upcoming classes' : undefined
                  }
                  emptyMonthDescription={
                    noAvailability ? emptyDescription : undefined
                  }
                />
                <Calendar.BookAction
                  classTitle={classData.name}
                  variant={
                    selectedWaitlistAction === 'view' || selectedBookedPath
                      ? 'secondary-gray'
                      : 'primary'
                  }
                  price={
                    bookPriceLabel ? (
                      <>
                        <Badge
                          label={bookPriceLabel}
                          size="md"
                          className="md:hidden"
                        />
                        <Badge
                          label={bookPriceLabel}
                          size="lg"
                          className="hidden md:inline-flex"
                        />
                      </>
                    ) : undefined
                  }
                />
              </>
            )}
          </Calendar.Root>
        )}
      </div>

      {bookingWarning && (
        <BookingWarningModal
          warning={bookingWarning}
          isOpen={bookingWarningOpen}
          onOpenChange={handleBookingWarningOpenChange}
          classTitle={classData.name}
          instructorName={getAbbreviatedInstructorName(
            bookingWarning.payload.instructorName
          )}
          startTime={bookingWarning.payload.startTime}
          timezone={bookingWarning.payload.timezone}
          onConfirm={handleBookingWarningConfirm}
          isOneTime={isOneTime}
        />
      )}

      {isLoginOpen && (
        <LoginDialog
          open
          onClose={() => setIsLoginOpen(false)}
          onConfirm={() => setIsLoginOpen(false)}
        />
      )}

      {waitlistSlot && (
        <JoinWaitlistModal
          onClose={() => setWaitlistSlot(null)}
          classData={classData}
          slot={{
            ...toOccurrenceKey(waitlistSlot),
            instructor_schedule_id: waitlistSlot.instructorScheduleId ?? '',
            timezone: waitlistSlot.timezone,
          }}
          instructorName={getAbbreviatedInstructorName(
            waitlistSlot.instructorName
          )}
        />
      )}
    </div>
  );
});
